/* eslint-disable i18next/no-literal-string */
import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Checkbox, RadioButton} from '@dropbox/dig-components/controls';
import {Popover} from '@dropbox/dig-components/popover';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Select, TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ChevronDownLine,
  ChevronRightLine,
  DeleteLine,
  WarningLine,
} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import classNames from 'classnames';
import {AdminWireframeService, ApiError} from 'client';
import {HeadcountChange} from 'client/models/HeadcountChange';
import {WorkstreamHeadcountDataRead} from 'client/models/WorkstreamHeadcountDataRead';
import {WorkstreamRow} from 'client/models/WorkstreamRow';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {
  isMultiCellClipboard,
  normalizePastedNumber,
  parseSpreadsheetClipboard,
} from 'helpers/spreadsheetClipboard';
import {
  AdjustmentValidation,
  AdjustmentValues,
  ALLOCATION_FIELDS,
  EMPTY_STAFFING_VALUES,
  fillBlankStaffingValues,
  formatStaffingCents,
  GEO_FIELDS,
  isBlankStaffingValue,
  isSameStaffingValue,
  STAFFING_FIELDS,
  StaffingField,
  StaffingValidation,
  StaffingValues,
  toAdjustmentPayload,
  toAdjustmentValues,
  toStaffingValues,
  validateAdjustments,
  validateStaffing,
} from 'helpers/staffingValues';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useSetAtom} from 'jotai';
import {ClipboardEvent, Dispatch, SetStateAction, useId, useState} from 'react';
import {getService} from 'utilities';
import {useTimeframes} from 'views/workstreams/detail/hooks';
import {getDefaultWorkstreamTimeframe} from 'views/workstreams/detail/workstreamTimeframe';
import {wireframeReportQueryKey} from 'views/workstreams/page/wireframes/wireframeReportQuery';

import styles from './AdminWireframeData.module.css';
import {
  buildWireframePillarHierarchy,
  buildWireframeWorkstreamHierarchy,
  listWireframePillars,
} from './wireframeWorkstreamHierarchy';

const WORKSTREAM_COLUMN_WIDTH = 300;
const INPUT_COLUMN_WIDTH = 88;
const ORG_COLUMN_WIDTH = 152;
const PROFILE_COLUMN_WIDTH = 180;
const ESTIMATED_COST_COLUMN_WIDTH = 140;
const CLEAR_COLUMN_WIDTH = 52;
const VALUE_FORMAT_ERROR = 'Enter a number with up to 2 decimal places';
const ALLOCATION_MISMATCH_ERROR = 'Sum of “Growth”, “CI”, and “KTLO” needs to match HC';
const HEADCOUNT_TOO_LARGE_ERROR = 'Sum of “Low”, “Mid”, and “High” must be less than 100,000,000';
const ADJUSTMENTS_ROW_ERROR = 'Fix adjustment errors to save';
const ADJUSTMENT_MISMATCH_ERROR =
  'Sum of “Growth”, “CI”, and “KTLO” adjustments needs to match the HC adjustment';
// Editable table columns in display order, so pasted spreadsheet cells fill left to right.
const EDITABLE_COLUMNS: {field: StaffingField; name: string; label: string}[] = [
  {field: 'allocation_growth_value', name: 'Growth', label: 'Growth allocation'},
  {field: 'allocation_ci_value', name: 'CI', label: 'CI allocation'},
  {field: 'allocation_ktlo_value', name: 'KTLO', label: 'KTLO allocation'},
  {field: 'geo_low_value', name: 'Low', label: 'Low geo distribution'},
  {field: 'geo_mid_value', name: 'Mid', label: 'Mid geo distribution'},
  {field: 'geo_high_value', name: 'High', label: 'High geo distribution'},
];
const formatUsd = (amount: number) =>
  amount.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
type Draft = {name: string; low: string; mid: string; high: string};
const emptyDraft: Draft = {name: '', low: '', mid: '', high: ''};
const toDraft = (profile: {
  name: string;
  annual_low_usd: number;
  annual_mid_usd: number;
  annual_high_usd: number;
}): Draft => ({
  name: profile.name,
  low: String(profile.annual_low_usd),
  mid: String(profile.annual_mid_usd),
  high: String(profile.annual_high_usd),
});

type InheritedOrg = {orgId: number; fromName: string};

type WireframeTableRow = {
  workstream: WorkstreamRow;
  pillarId: number;
  depth: number;
  /** The row's own org in this pillar, including a pending save. */
  directOrgId: number | null;
  /** The nearest ancestor's org on the path this pillar shows the row under. */
  inheritedOrg: InheritedOrg | null;
  headcount: WorkstreamHeadcountDataRead | null;
  profileName: string | null;
  estimatedCost: number | null;
  /** The cost the saved adjustments remove, or null without Low, Mid or High adjustments. */
  adjustedCost: number | null;
};

// Headcount and orgs are entered per workstream within a pillar.
const toRowKey = (pillarId: number, workstreamId: number) => `${pillarId}:${workstreamId}`;
const fromRowKey = (key: string) => {
  const [pillarId, workstreamId] = key.split(':').map(Number);
  return {pillar_workstream_id: pillarId, workstream_id: workstreamId};
};

// A row with every input blank has no headcount; saving it clears the stored row.
const isBlankStaffing = (values: StaffingValues) =>
  STAFFING_FIELDS.every((field) => isBlankStaffingValue(values[field]));

const pluralizeWorkstreams = (count: number) =>
  `${count} ${count === 1 ? 'workstream' : 'workstreams'}`;

const pluralizeRows = (count: number) => `${count} ${count === 1 ? 'row' : 'rows'}`;

const effectiveGeoValue = (
  headcount: WorkstreamHeadcountDataRead | null,
  geo: 'low' | 'mid' | 'high'
) => {
  const value = Number(toStaffingValues(headcount)[`geo_${geo}_value`]);
  return Number.isFinite(value) ? value : 0;
};

type GeoRates = {annual_low_usd: number; annual_mid_usd: number; annual_high_usd: number};
// Half of the annual cost, since headcount is entered per half.
const estimateCost = (geoValues: Record<'low' | 'mid' | 'high', number>, profile: GeoRates) =>
  0.5 *
  (geoValues.low * profile.annual_low_usd +
    geoValues.mid * profile.annual_mid_usd +
    geoValues.high * profile.annual_high_usd);

type HeadcountSaveError = {
  /** Index of the failing change in the request, when the error points at one. */
  changeIndex: number | null;
  message: string;
};

// Server 422s list each failed rule; show their messages as written.
const getHeadcountSaveErrors = (error: Error): HeadcountSaveError[] => {
  const detail = error instanceof ApiError && error.status === 422 ? error.body?.detail : null;
  if (typeof detail === 'string') return [{changeIndex: null, message: detail}];
  if (!Array.isArray(detail)) return [];
  return detail
    .map((item) => {
      // FastAPI locates each error as ["body", "changes", <index>, ...].
      const [source, field, index] = Array.isArray(item?.loc) ? item.loc : [];
      return {
        changeIndex:
          source === 'body' && field === 'changes' && typeof index === 'number' ? index : null,
        message: typeof item?.msg === 'string' ? item.msg.replace(/^Value error, /, '') : '',
      };
    })
    .filter(({message}) => message);
};

export const AdminWireframeData = () => {
  useDocumentTitle('Wireframe Data');
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const timeframes = useTimeframes();
  // Empty until a timeframe is picked; the default is known once the timeframes load.
  const [pickedTimeframe, setPickedTimeframe] = useState('');
  const timeframe = pickedTimeframe || getDefaultWorkstreamTimeframe(new Date(), timeframes);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [newDraft, setNewDraft] = useState(emptyDraft);
  const [selectedWorkstreams, setSelectedWorkstreams] = useState<number[]>([]);
  const [headcountDrafts, setHeadcountDrafts] = useState<Record<string, StaffingValues>>({});
  // Kept apart from the base values, and merged into every saved row so no save drops them.
  const [adjustmentDrafts, setAdjustmentDrafts] = useState<Record<string, AdjustmentValues>>({});
  const [expandedAdjustments, setExpandedAdjustments] = useState<Set<string>>(new Set());
  const [selectedPillarId, setSelectedPillarId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [workstreamPickerOpen, setWorkstreamPickerOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  // The org each pillar row is being saved to, by row key; shown, and its select disabled,
  // until the save settles. Rows below it inherit the pending org meanwhile. A failed save
  // drops the entry, reverting the row to the saved org.
  const [pendingOrgIds, setPendingOrgIds] = useState<Record<string, number | null>>({});
  const dataQuery = useQuery({
    queryKey: ['admin-wireframe-data', timeframe],
    queryFn: () =>
      getService(AdminWireframeService).getWireframeDataApiV1AdminWireframeDataGet(timeframe),
    enabled: Boolean(timeframe),
  });
  const isLoadingData = dataQuery.isLoading || !timeframe;
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: ['admin-wireframe-data', timeframe]}),
      // The /workstreams Wireframes report is computed from these inputs.
      queryClient.invalidateQueries({queryKey: wireframeReportQueryKey(timeframe)}),
    ]);
    setSnackbar({text: 'Changes saved.'});
  };
  const createMutation = useMutation({
    mutationFn: () =>
      getService(AdminWireframeService).createCostProfileApiV1AdminWireframeCostProfilesPost(
        {
          name: newDraft.name.trim(),
          annual_low_usd: Number(newDraft.low),
          annual_mid_usd: Number(newDraft.mid),
          annual_high_usd: Number(newDraft.high),
        },
        timeframe
      ),
    onSuccess: async (profile) => {
      await refresh();
      setNewDraft(emptyDraft);
      setSelectedProfileId(profile.id);
      setSelectedWorkstreams([]);
      setDraft(toDraft(profile));
    },
  });
  const saveMutation = useMutation({
    mutationFn: () =>
      getService(
        AdminWireframeService
      ).updateCostProfileApiV1AdminWireframeCostProfilesProfileIdPut(
        selectedProfileId!,
        {
          name: draft.name.trim(),
          annual_low_usd: Number(draft.low),
          annual_mid_usd: Number(draft.mid),
          annual_high_usd: Number(draft.high),
        },
        timeframe
      ),
    onSuccess: refresh,
  });
  const archiveMutation = useMutation({
    mutationFn: (profileId: number) =>
      getService(
        AdminWireframeService
      ).archiveCostProfileApiV1AdminWireframeCostProfilesProfileIdArchivePost(profileId, timeframe),
    onSuccess: async () => {
      setSelectedProfileId(null);
      setDraft(emptyDraft);
      await refresh();
    },
  });
  const applyMutation = useMutation({
    mutationFn: () =>
      getService(
        AdminWireframeService
      ).patchCostProfileAssignmentsApiV1AdminWireframeCostProfileAssignmentsPatch(
        {
          changes: assignmentChanges,
        },
        timeframe
      ),
    onSuccess: refresh,
  });
  const headcountMutation = useMutation({
    // The submitted changes stay on the mutation, so a 422 can name the row it points at.
    mutationFn: (changes: HeadcountChange[]) =>
      getService(
        AdminWireframeService
      ).patchWireframeHeadcountDataApiV1AdminWireframeHeadcountDataPatch({changes}, timeframe),
    onSuccess: async () => {
      setHeadcountDrafts({});
      setAdjustmentDrafts({});
      await refresh();
    },
  });
  const createOrgMutation = useMutation({
    mutationFn: () =>
      getService(AdminWireframeService).createOrgApiV1AdminWireframeOrgsPost(
        {name: orgName.trim()},
        timeframe
      ),
    onSuccess: async () => {
      setOrgName('');
      await refresh();
    },
  });
  const archiveOrgMutation = useMutation({
    mutationFn: ({orgId}: {orgId: number; name: string}) =>
      getService(AdminWireframeService).archiveOrgApiV1AdminWireframeOrgsOrgIdArchivePost(
        orgId,
        timeframe
      ),
    onSuccess: refresh,
    onError: (error) => {
      // Someone else already deleted it; reload so the list stops showing it.
      if (error instanceof ApiError && error.status === 404)
        void queryClient.invalidateQueries({queryKey: ['admin-wireframe-data', timeframe]});
    },
  });
  const orgAssignmentMutation = useMutation({
    mutationFn: ({
      pillarId,
      workstreamId,
      orgId,
    }: {
      pillarId: number;
      workstreamId: number;
      orgId: number | null;
      name: string;
    }) =>
      getService(AdminWireframeService).patchOrgAssignmentsApiV1AdminWireframeOrgAssignmentsPatch(
        {
          changes: [{pillar_workstream_id: pillarId, workstream_id: workstreamId, org_id: orgId}],
        },
        timeframe
      ),
    onMutate: ({pillarId, workstreamId, orgId}) =>
      setPendingOrgIds((current) => ({...current, [toRowKey(pillarId, workstreamId)]: orgId})),
    onSuccess: refresh,
    onError: (_error, {name}) =>
      setSnackbar({text: `Could not save the org for “${name}”. Try again.`}),
    onSettled: (_result, _error, {pillarId, workstreamId}) =>
      setPendingOrgIds((current) => {
        const remaining = {...current};
        delete remaining[toRowKey(pillarId, workstreamId)];
        return remaining;
      }),
  });
  const data = dataQuery.data;
  const orgs = [...(data?.orgs ?? [])]
    .filter((org) => !org.archived_at)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}) || a.id - b.id);
  const activeOrgIds = new Set(orgs.map(({id}) => id));
  // Each pillar row's own org, saved or pending. Assignments to orgs that are no longer active
  // are left out, so rows inherit past them, as the report does.
  const directOrgIds = new Map<string, number>();
  data?.workstreams.forEach(({workstream_id, org_assignments}) =>
    org_assignments.forEach(({pillar_workstream_id, org_id}) => {
      if (activeOrgIds.has(org_id))
        directOrgIds.set(toRowKey(pillar_workstream_id, workstream_id), org_id);
    })
  );
  Object.entries(pendingOrgIds).forEach(([key, orgId]) => {
    if (orgId == null) directOrgIds.delete(key);
    else directOrgIds.set(key, orgId);
  });
  const workstreamNames = new Map(data?.workstreams.map((row) => [row.workstream_id, row.name]));
  const resolveOrg = (pillarId: number, workstreamId: number, ancestorIds: number[]) => {
    const directOrgId = directOrgIds.get(toRowKey(pillarId, workstreamId)) ?? null;
    const fromId = [...ancestorIds]
      .reverse()
      .find((ancestorId) => directOrgIds.has(toRowKey(pillarId, ancestorId)));
    const inheritedOrg: InheritedOrg | null =
      fromId == null
        ? null
        : {
            orgId: directOrgIds.get(toRowKey(pillarId, fromId))!,
            fromName: workstreamNames.get(fromId) ?? '',
          };
    return {directOrgId, inheritedOrg};
  };
  const archiveOrgStatus =
    archiveOrgMutation.error instanceof ApiError ? archiveOrgMutation.error.status : null;
  const archivedOrgName = archiveOrgMutation.variables?.name ?? 'this org';
  const orgArchiveError = !archiveOrgMutation.error
    ? null
    : archiveOrgStatus === 409
      ? `“${archivedOrgName}” is still assigned to workstreams. Clear it in the Org column, then try again.`
      : archiveOrgStatus === 404
        ? `“${archivedOrgName}” was already deleted.`
        : `Could not delete “${archivedOrgName}”. Try again.`;
  const orgCreateStatus =
    createOrgMutation.error instanceof ApiError ? createOrgMutation.error.status : null;
  const orgCreateError =
    orgCreateStatus === 409
      ? `An org named “${orgName.trim()}” already exists for ${timeframe}.`
      : // The report's row for workstreams without an org owns this name.
        orgCreateStatus === 422 && orgName.trim().toLowerCase() === 'unassigned'
        ? '“Unassigned” is reserved for workstreams without an org.'
        : createOrgMutation.error
          ? 'Could not create this org. Try again.'
          : null;
  const profiles = data?.profiles.filter((profile) => !profile.archived_at) ?? [];
  const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId);
  const assignedWorkstreamIds = new Set(
    data?.assignments
      .filter((assignment) => assignment.profile_id === selectedProfileId)
      .map((assignment) => assignment.workstream_id) ?? []
  );
  const assignmentChanges = [
    ...selectedWorkstreams
      .filter((workstreamId) => !assignedWorkstreamIds.has(workstreamId))
      .map((workstream_id) => ({workstream_id, profile_id: selectedProfileId})),
    ...[...assignedWorkstreamIds]
      .filter((workstreamId) => !selectedWorkstreams.includes(workstreamId))
      .map((workstream_id) => ({workstream_id, profile_id: null})),
  ];
  const selectProfile = (profileId: number) => {
    const profile = profiles.find(({id}) => id === profileId);
    setSelectedProfileId(profileId);
    if (profile) setDraft(toDraft(profile));
    setSelectedWorkstreams(
      data?.assignments
        .filter((assignment) => assignment.profile_id === profileId)
        .map((assignment) => assignment.workstream_id) ?? []
    );
  };
  const assignedNames = (profileId: number) => {
    const ids = new Set(
      data?.assignments
        .filter((item) => item.profile_id === profileId)
        .map((item) => item.workstream_id)
    );
    return (
      data?.workstreams.filter((row) => ids.has(row.workstream_id)).map((row) => row.name) ?? []
    );
  };
  const orderedWorkstreams = data
    ? buildWireframeWorkstreamHierarchy(data.workstreams, data.narrative_root_workstream_id)
    : [];
  const filteredWorkstreams = orderedWorkstreams.filter(({workstream}) =>
    workstream.name.toLowerCase().includes(search.toLowerCase())
  );
  const pillars = data
    ? listWireframePillars(data.workstreams, data.narrative_root_workstream_id)
    : [];
  const activePillarId = pillars.some(({workstream_id}) => workstream_id === selectedPillarId)
    ? selectedPillarId
    : (pillars[0]?.workstream_id ?? null);
  // Rows of every pillar each org applies to, its own or inherited. A workstream under two
  // pillars is two rows, each with its own org. Only rows set directly block deleting the org.
  const orgRowCounts = new Map<number, {total: number; direct: number}>();
  pillars.forEach(({workstream_id: pillarId}) =>
    buildWireframePillarHierarchy(data!.workstreams, pillarId).forEach(
      ({workstream, ancestorIds}) => {
        const {directOrgId, inheritedOrg} = resolveOrg(
          pillarId,
          workstream.workstream_id,
          ancestorIds
        );
        const orgId = directOrgId ?? inheritedOrg?.orgId;
        if (orgId == null) return;
        const counts = orgRowCounts.get(orgId) ?? {total: 0, direct: 0};
        counts.total += 1;
        if (directOrgId != null) counts.direct += 1;
        orgRowCounts.set(orgId, counts);
      }
    )
  );
  const directProfileIds = new Map(
    data?.workstreams.map(({workstream_id, direct_profile_id}) => [
      workstream_id,
      direct_profile_id,
    ]) ?? []
  );
  const tableRows: WireframeTableRow[] = (
    data && activePillarId != null
      ? buildWireframePillarHierarchy(data.workstreams, activePillarId)
      : []
  ).map(({workstream, depth, ancestorIds}) => {
    // Inherit along the path this pillar's table shows the row under.
    const effectiveProfileId =
      workstream.direct_profile_id ??
      [...ancestorIds]
        .reverse()
        .map((ancestorId) => directProfileIds.get(ancestorId))
        .find((profileId) => profileId != null) ??
      null;
    const profile = profiles.find(({id}) => id === effectiveProfileId);
    const pillarId = activePillarId!;
    const headcount =
      workstream.headcount_data.find(
        ({pillar_workstream_id}) => pillar_workstream_id === pillarId
      ) ?? null;
    const estimatedCost =
      headcount && profile
        ? estimateCost(
            {
              low: effectiveGeoValue(headcount, 'low'),
              mid: effectiveGeoValue(headcount, 'mid'),
              high: effectiveGeoValue(headcount, 'high'),
            },
            profile
          )
        : null;
    const adjustments = toAdjustmentValues(headcount);
    const adjustedCost =
      profile && GEO_FIELDS.some((field) => !isBlankStaffingValue(adjustments[field]))
        ? estimateCost(
            {
              low: Number(adjustments.geo_low_value || 0),
              mid: Number(adjustments.geo_mid_value || 0),
              high: Number(adjustments.geo_high_value || 0),
            },
            profile
          )
        : null;
    return {
      workstream,
      pillarId,
      depth,
      ...resolveOrg(pillarId, workstream.workstream_id, ancestorIds),
      headcount,
      profileName: profile?.name ?? null,
      estimatedCost,
      adjustedCost,
    };
  });
  const tableColumns: Column<WireframeTableRow>[] = [
    {type: 'Workstream', minWidth: WORKSTREAM_COLUMN_WIDTH, spanner: true},
    {type: 'Org', fixed: true, minWidth: ORG_COLUMN_WIDTH},
    ...(['HC', 'Growth', 'CI', 'KTLO', 'Low', 'Mid', 'High'] as const).map((type) => ({
      type,
      fixed: true,
      minWidth: INPUT_COLUMN_WIDTH,
    })),
    {type: 'Profile', fixed: true, minWidth: PROFILE_COLUMN_WIDTH},
    {type: 'Est. cost', fixed: true, minWidth: ESTIMATED_COST_COLUMN_WIDTH},
    {type: 'Clear', fixed: true, minWidth: CLEAR_COLUMN_WIDTH},
  ];
  const unsavedHintId = useId();
  const adjustmentErrorIdPrefix = useId();
  const orgCreateErrorId = useId();
  const orgInUseHintPrefix = useId();
  const errorHintIds = {
    [VALUE_FORMAT_ERROR]: useId(),
    [ALLOCATION_MISMATCH_ERROR]: useId(),
    [HEADCOUNT_TOO_LARGE_ERROR]: useId(),
    [ADJUSTMENTS_ROW_ERROR]: useId(),
  };
  const savedHeadcounts = new Map(
    data?.workstreams.flatMap(({workstream_id, headcount_data}) =>
      headcount_data.map(
        (headcount) => [toRowKey(headcount.pillar_workstream_id, workstream_id), headcount] as const
      )
    ) ?? []
  );
  const getRowKey = (row: WireframeTableRow) =>
    toRowKey(row.pillarId, row.workstream.workstream_id);
  // Rows without saved data show blank cells rather than zeros until a value is entered.
  const getSavedHeadcount = (key: string) => {
    const saved = savedHeadcounts.get(key);
    return saved ? toStaffingValues(saved) : EMPTY_STAFFING_VALUES;
  };
  const getHeadcountDraft = (key: string): StaffingValues =>
    headcountDrafts[key] ?? getSavedHeadcount(key);
  const getSavedAdjustments = (key: string) => toAdjustmentValues(savedHeadcounts.get(key));
  const getAdjustmentDraft = (key: string): AdjustmentValues =>
    adjustmentDrafts[key] ?? getSavedAdjustments(key);
  // A draft is kept only while it differs from what is saved.
  const setRowDraft = (
    setDrafts: Dispatch<SetStateAction<Record<string, StaffingValues>>>,
    key: string,
    next: StaffingValues,
    saved: StaffingValues
  ) => {
    const matchesSaved = STAFFING_FIELDS.every((field) =>
      isSameStaffingValue(next[field], saved[field])
    );
    setDrafts((current) => {
      if (matchesSaved) {
        const remaining = {...current};
        delete remaining[key];
        return remaining;
      }
      return {...current, [key]: next};
    });
  };
  // Opens a row whose adjustments a finished edit made invalid, so the error that blocks saving is
  // shown. It then stays open until the caret closes it, even once the error is fixed. Typed base
  // values are checked when their cell is left, since a half-typed value can briefly look invalid.
  const openAdjustmentsIfInvalid = (
    key: string,
    values: StaffingValues,
    adjustments: AdjustmentValues
  ) => {
    if (isBlankStaffing(values)) return;
    if (validateAdjustments(fillBlankStaffingValues(values), adjustments).isValid) return;
    setExpandedAdjustments((current) => (current.has(key) ? current : new Set(current).add(key)));
  };
  const updateHeadcount = (row: WireframeTableRow, values: Partial<StaffingValues>) => {
    const key = getRowKey(row);
    const next = {...getHeadcountDraft(key), ...values};
    setRowDraft(setHeadcountDrafts, key, next, getSavedHeadcount(key));
    return next;
  };
  const updateAdjustments = (row: WireframeTableRow, values: Partial<AdjustmentValues>) => {
    const key = getRowKey(row);
    // Adjustments are only editable while their row is open, so they never need to open it.
    setRowDraft(
      setAdjustmentDrafts,
      key,
      {...getAdjustmentDraft(key), ...values},
      getSavedAdjustments(key)
    );
  };
  // Adjustments only exist on a row with entered data, and saving a blank row clears them.
  const hasAdjustmentsRow = (key: string) => !isBlankStaffing(getHeadcountDraft(key));
  const validateAdjustmentDraft = (key: string): AdjustmentValidation | null =>
    hasAdjustmentsRow(key)
      ? validateAdjustments(
          fillBlankStaffingValues(getHeadcountDraft(key)),
          getAdjustmentDraft(key)
        )
      : null;
  // A blank row saves as a clear, so adjustment drafts left on it are not changes of their own.
  const changedHeadcountKeys = [
    ...new Set([
      ...Object.keys(headcountDrafts),
      ...Object.keys(adjustmentDrafts).filter(hasAdjustmentsRow),
    ]),
  ];
  const buildHeadcountChanges = (): HeadcountChange[] =>
    changedHeadcountKeys.map((key) => {
      const values = getHeadcountDraft(key);
      return {
        ...fromRowKey(key),
        // Each change replaces the whole row, so it always carries the row's adjustments.
        data: isBlankStaffing(values)
          ? null
          : {
              ...fillBlankStaffingValues(values),
              ...toAdjustmentPayload(getAdjustmentDraft(key)),
            },
      };
    });
  // Fills a block copied from a spreadsheet rightward and downward from the pasted cell, dropping
  // anything that falls outside the table. Single values paste natively. An Adjustments row takes
  // only the first line, since the next line would land on another workstream's base values.
  const pasteHeadcount = (
    event: ClipboardEvent<HTMLInputElement>,
    row: WireframeTableRow,
    field: StaffingField,
    isAdjustment = false
  ) => {
    const text = event.clipboardData.getData('text/plain');
    if (!isMultiCellClipboard(text)) return;
    event.preventDefault();
    const startRow = tableRows.indexOf(row);
    const startColumn = EDITABLE_COLUMNS.findIndex((column) => column.field === field);
    const lines = parseSpreadsheetClipboard(text);
    (isAdjustment ? lines.slice(0, 1) : lines).forEach((cells, rowOffset) => {
      const target = tableRows[startRow + rowOffset];
      if (!target) return;
      const values: Partial<StaffingValues> = {};
      cells.forEach((cell, columnOffset) => {
        const column = EDITABLE_COLUMNS[startColumn + columnOffset];
        if (column) values[column.field] = normalizePastedNumber(cell);
      });
      if (isAdjustment) {
        updateAdjustments(target, values);
        return;
      }
      // A paste lands whole, so its values are already final.
      const targetKey = getRowKey(target);
      openAdjustmentsIfInvalid(
        targetKey,
        updateHeadcount(target, values),
        getAdjustmentDraft(targetKey)
      );
    });
  };
  const getCellError = (validation: StaffingValidation, field: StaffingField) => {
    if (validation.invalidFields.has(field)) return VALUE_FORMAT_ERROR;
    if (validation.allocationMismatch && (ALLOCATION_FIELDS as readonly string[]).includes(field))
      return ALLOCATION_MISMATCH_ERROR;
    if (validation.headcountTooLarge && (GEO_FIELDS as readonly string[]).includes(field))
      return HEADCOUNT_TOO_LARGE_ERROR;
    return null;
  };
  const getAdjustmentCellError = (
    validation: AdjustmentValidation,
    field: StaffingField,
    name: string
  ) => {
    if (validation.invalidFields.has(field)) return VALUE_FORMAT_ERROR;
    const baseCents = validation.exceededFields.get(field);
    if (baseCents != null) return `Can’t exceed ${name} (${formatStaffingCents(baseCents)})`;
    if (validation.allocationMismatch && (ALLOCATION_FIELDS as readonly string[]).includes(field))
      return ADJUSTMENT_MISMATCH_ERROR;
    return null;
  };
  const renderCellInput = ({
    label,
    value,
    unsaved,
    error,
    tooltipMessage,
    errorHintId,
    onChange,
    onPaste,
    onBlur,
  }: {
    label: string;
    value: string;
    unsaved: boolean;
    error: string | null;
    tooltipMessage: string | null;
    errorHintId: string | undefined;
    onChange: (value: string) => void;
    onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
  }) => {
    const describedBy = [unsaved && unsavedHintId, error && errorHintId].filter(Boolean).join(' ');
    // Always render the tooltip wrapper so toggling the error never remounts a focused input.
    return (
      <Tooltip title={tooltipMessage ?? ''} placement="top" isDisabled={!error}>
        <span className={styles.tableCellTooltipTrigger}>
          <TextInput
            size="small"
            type="number"
            min={0}
            step={0.01}
            inputMode="decimal"
            wrapperProps={{
              className: classNames(styles.tableCellInput, {
                [styles.unsaved]: unsaved,
                [styles.invalid]: error,
              }),
            }}
            aria-label={label}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : undefined}
            value={value}
            onChange={(event) => onChange(event.currentTarget.value)}
            onPaste={onPaste}
            onBlur={onBlur}
          />
        </span>
      </Tooltip>
    );
  };
  const renderNumberInput = (row: WireframeTableRow, field: StaffingField, label: string) => {
    const key = getRowKey(row);
    const rowDraft = getHeadcountDraft(key);
    const validation = validateStaffing(fillBlankStaffingValues(rowDraft));
    const error = getCellError(validation, field);
    return renderCellInput({
      label: `${row.workstream.name} ${label}`,
      value: rowDraft[field],
      unsaved: !isSameStaffingValue(rowDraft[field], getSavedHeadcount(key)[field]),
      error,
      tooltipMessage:
        error === ALLOCATION_MISMATCH_ERROR && validation.allocationCents != null
          ? `${error} (current sum: ${formatStaffingCents(validation.allocationCents)})`
          : error,
      errorHintId: error ? errorHintIds[error] : undefined,
      onChange: (value) => updateHeadcount(row, {[field]: value}),
      onPaste: (event) => pasteHeadcount(event, row, field),
      onBlur: () => openAdjustmentsIfInvalid(key, rowDraft, getAdjustmentDraft(key)),
    });
  };
  const renderAdjustmentInput = (row: WireframeTableRow, field: StaffingField, name: string) => {
    const key = getRowKey(row);
    const values = getAdjustmentDraft(key);
    const validation = validateAdjustmentDraft(key);
    const error = validation && getAdjustmentCellError(validation, field, name);
    // Bound errors name the base value, so each cell carries its own description.
    const errorHintId = `${adjustmentErrorIdPrefix}${key}-${field}`;
    return (
      <>
        {renderCellInput({
          label: `${name} adjustment for ${row.workstream.name}`,
          value: values[field],
          unsaved: !isSameStaffingValue(values[field], getSavedAdjustments(key)[field]),
          error,
          tooltipMessage:
            error === ADJUSTMENT_MISMATCH_ERROR && validation?.allocationCents != null
              ? `${error} (current sum: ${formatStaffingCents(validation.allocationCents)})`
              : error,
          errorHintId,
          onChange: (value) => updateAdjustments(row, {[field]: value}),
          onPaste: (event) => pasteHeadcount(event, row, field, true),
        })}
        {error && (
          <span id={errorHintId} hidden>
            {error}
          </span>
        )}
      </>
    );
  };
  const renderHeadcount = (row: WireframeTableRow) => {
    const rowDraft = getHeadcountDraft(getRowKey(row));
    const {headcountCents} = validateStaffing(fillBlankStaffingValues(rowDraft));
    const isUnset = GEO_FIELDS.every((field) => isBlankStaffingValue(rowDraft[field]));
    return (
      <Text
        tagName="output"
        size="small"
        className={styles.tableCellValue}
        aria-label={`${row.workstream.name} headcount`}
      >
        {isUnset ? '' : headcountCents == null ? '—' : formatStaffingCents(headcountCents)}
      </Text>
    );
  };
  // The headcount the adjustments remove: Low + Mid + High, blank until one is entered.
  const renderAdjustmentHeadcount = (row: WireframeTableRow) => {
    const key = getRowKey(row);
    const values = getAdjustmentDraft(key);
    const isUnset = GEO_FIELDS.every((field) => isBlankStaffingValue(values[field]));
    const headcountCents = validateAdjustmentDraft(key)?.headcountCents ?? null;
    return (
      <Text
        tagName="output"
        size="small"
        className={styles.tableCellValue}
        aria-label={`Headcount adjustment for ${row.workstream.name}`}
      >
        {isUnset ? '' : headcountCents == null ? '—' : formatStaffingCents(headcountCents)}
      </Text>
    );
  };
  // Blanks the base inputs; once saved, the row's stored headcount and adjustments are deleted.
  const renderClearHeadcount = (row: WireframeTableRow) => {
    if (isBlankStaffing(getHeadcountDraft(getRowKey(row)))) return null;
    const label = `Clear ${row.workstream.name} headcount`;
    return (
      <Tooltip title="Clear headcount" placement="top">
        <IconButton
          variant="borderless"
          size="small"
          aria-label={label}
          onClick={() => updateHeadcount(row, EMPTY_STAFFING_VALUES)}
        >
          <UIIcon src={DeleteLine} size="small" />
        </IconButton>
      </Tooltip>
    );
  };
  const isAdjustmentsRowOpen = (key: string) =>
    hasAdjustmentsRow(key) && expandedAdjustments.has(key);
  const renderAdjustmentsToggle = (row: WireframeTableRow) => {
    const key = getRowKey(row);
    if (!hasAdjustmentsRow(key)) return <span className={styles.adjustmentsTogglePlaceholder} />;
    const isOpen = isAdjustmentsRowOpen(key);
    // A closed row hides its cell errors, so the caret says why Save is blocked.
    const hasHiddenErrors = !isOpen && validateAdjustmentDraft(key)?.isValid === false;
    // Always render the tooltip wrapper so toggling the cue never remounts the focused caret.
    return (
      <Tooltip title={ADJUSTMENTS_ROW_ERROR} placement="top" isDisabled={!hasHiddenErrors}>
        <span className={styles.adjustmentsToggleTrigger}>
          <IconButton
            variant="borderless"
            size="small"
            aria-label={`${row.workstream.name} adjustments`}
            aria-expanded={isOpen}
            aria-describedby={hasHiddenErrors ? errorHintIds[ADJUSTMENTS_ROW_ERROR] : undefined}
            onClick={() =>
              setExpandedAdjustments((current) => {
                const next = new Set(current);
                if (isOpen) next.delete(key);
                else next.add(key);
                return next;
              })
            }
          >
            <UIIcon
              src={hasHiddenErrors ? WarningLine : isOpen ? ChevronDownLine : ChevronRightLine}
              size="small"
              className={hasHiddenErrors ? styles.adjustmentsWarningIcon : undefined}
            />
          </IconButton>
        </span>
      </Tooltip>
    );
  };
  const renderAdjustmentsRow = (row: WireframeTableRow) => (
    <Table.Row
      key={`${row.workstream.workstream_id}-adjustments`}
      className={styles.adjustmentsRow}
    >
      <Table.Cell>
        <Box className={styles.adjustmentsLabel} style={{paddingLeft: Math.min(row.depth, 5) * 16}}>
          <Text size="small" color="subtle">
            Adjustments
          </Text>
        </Box>
      </Table.Cell>
      {/* Org is set on the workstream row itself; adjustments have none. */}
      <Table.Cell />
      <Table.Cell style={{padding: 0}}>{renderAdjustmentHeadcount(row)}</Table.Cell>
      {EDITABLE_COLUMNS.map(({field, name}) => (
        <Table.Cell key={field} style={{padding: 0}}>
          {renderAdjustmentInput(row, field, name)}
        </Table.Cell>
      ))}
      <Table.Cell />
      <Table.Cell>
        <Text
          tagName="output"
          size="small"
          color="subtle"
          aria-label={`Cost adjustment for ${row.workstream.name}`}
        >
          {row.adjustedCost == null
            ? '—'
            : // Signed from the rounded amount, so a few cents never show as "−$0".
              Math.round(row.adjustedCost) > 0
              ? `−${formatUsd(row.adjustedCost)}`
              : formatUsd(0)}
        </Text>
      </Table.Cell>
      <Table.Cell />
    </Table.Row>
  );
  // The row's own org, else the one it inherits, shown muted. Picking an org overrides the
  // inherited one for this row and the rows below it; the inherited option resets it.
  const renderOrgSelect = (row: WireframeTableRow) => {
    const {workstream_id: workstreamId, name} = row.workstream;
    const isPending = getRowKey(row) in pendingOrgIds;
    const {directOrgId, inheritedOrg} = row;
    const inheritedName = inheritedOrg && orgs.find(({id}) => id === inheritedOrg.orgId)?.name;
    const isInheriting = directOrgId == null && inheritedName != null;
    const select = (
      <Select
        id={`wireframe-org-${row.pillarId}-${workstreamId}`}
        isNative
        size="small"
        className={classNames(styles.orgSelect, {[styles.orgSelectInherited]: isInheriting})}
        aria-label={`${name} org`}
        value={directOrgId == null ? '' : String(directOrgId)}
        disabled={isPending}
        onChange={(value) =>
          orgAssignmentMutation.mutate({
            pillarId: row.pillarId,
            workstreamId,
            orgId: value ? Number(value) : null,
            name,
          })
        }
      >
        <Select.Option value="">
          {inheritedName ? `${inheritedName} (inherited)` : '—'}
        </Select.Option>
        {orgs.map((org) => (
          <Select.Option key={org.id} value={String(org.id)}>
            {org.name}
          </Select.Option>
        ))}
      </Select>
    );
    return isInheriting ? (
      <Tooltip title={`Inherited from “${inheritedOrg!.fromName}”`} placement="top">
        <div>{select}</div>
      </Tooltip>
    ) : (
      select
    );
  };
  const hasHeadcountChanges = changedHeadcountKeys.length > 0;
  const hasInvalidHeadcount = changedHeadcountKeys.some(
    (key) =>
      !validateStaffing(fillBlankStaffingValues(getHeadcountDraft(key))).isValid ||
      validateAdjustmentDraft(key)?.isValid === false
  );
  const headcountSaveErrors = (
    headcountMutation.error ? getHeadcountSaveErrors(headcountMutation.error) : []
  ).map(({changeIndex, message}) => {
    const change = changeIndex == null ? undefined : headcountMutation.variables?.[changeIndex];
    const name = change && workstreamNames.get(change.workstream_id);
    return {
      key: `${changeIndex ?? 'request'}:${message}`,
      text: name ? `${name}: ${message}` : message,
    };
  });
  const updateDraft = (key: keyof Draft, value: string, isNew = false) =>
    (isNew ? setNewDraft : setDraft)((current) => ({...current, [key]: value}));
  const valid = (value: Draft) =>
    value.name.trim() && [value.low, value.mid, value.high].every((rate) => /^\d+$/.test(rate));

  const renderFields = (value: Draft, setter: (key: keyof Draft, value: string) => void) => (
    <div className={styles.fields}>
      <TextInput
        aria-label="Profile name"
        placeholder="New profile name"
        value={value.name}
        onChange={(event) => setter('name', event.currentTarget.value)}
      />
      {(['low', 'mid', 'high'] as const).map((geo) => (
        <TextInput
          key={geo}
          aria-label={`${geo} annual cost per HC`}
          type="number"
          placeholder={`${geo[0].toUpperCase()}${geo.slice(1)} $`}
          value={value[geo]}
          onChange={(event) => setter(geo, event.currentTarget.value)}
        />
      ))}
    </div>
  );

  return (
    <Layout.Container width="100%">
      <main className={styles.page}>
        <span id={unsavedHintId} hidden>
          Unsaved change
        </span>
        {Object.entries(errorHintIds).map(([message, id]) => (
          <span key={id} id={id} hidden>
            {message}
          </span>
        ))}
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Text tagName="span" size="small" color="subtle">
            Admin
          </Text>
          <Text tagName="span" size="small" color="subtle">
            /
          </Text>
          <Text tagName="span" size="small">
            Wireframe Data
          </Text>
        </nav>
        <header className={styles.pageHeader}>
          <Title>Wireframe Data</Title>
          <Select
            id="planning-period"
            aria-label="Planning period"
            value={timeframe}
            onChange={(value) => {
              setPickedTimeframe(value);
              setSelectedProfileId(null);
              setSelectedWorkstreams([]);
              // Drafts belong to the half they were entered for.
              setHeadcountDrafts({});
              setAdjustmentDrafts({});
              setExpandedAdjustments(new Set());
              setOrgName('');
              createOrgMutation.reset();
              archiveOrgMutation.reset();
            }}
          >
            {timeframes.map(({name}) => (
              <Select.Option key={name} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </header>
        <div className={styles.cards}>
          <section className={styles.panel} aria-labelledby="cost-profiles-heading">
            <div className={styles.panelHeader}>
              <Text id="cost-profiles-heading" variant="label" size="large" isBold>
                Cost input profiles
              </Text>
              <Text tagName="p" color="subtle" size="small">
                Annual cost per HC for Low, Mid and High geos. Select a profile to edit it or apply
                it.
              </Text>
              <form
                className={styles.addRow}
                onSubmit={(event) => {
                  event.preventDefault();
                  createMutation.mutate();
                }}
              >
                {renderFields(newDraft, (key, value) => updateDraft(key, value, true))}
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!valid(newDraft) || createMutation.isPending}
                >
                  ＋ Create
                </Button>
              </form>
            </div>
            {isLoadingData ? (
              <Text className={styles.status} color="subtle">
                Loading cost profiles…
              </Text>
            ) : null}
            {dataQuery.error ? (
              <SystemBanner tone="alert">
                <Text>Unable to load profiles. Your Finance access may be required.</Text>
              </SystemBanner>
            ) : null}
            {profiles.map((profile) => {
              const active = profile.id === selectedProfileId;
              const savedDraft = toDraft(profile);
              const isUnsaved = (key: keyof Draft) => active && draft[key] !== savedDraft[key];
              const hasUnsavedChanges = (Object.keys(savedDraft) as (keyof Draft)[]).some(
                isUnsaved
              );
              const names = assignedNames(profile.id);
              return (
                <div className={styles.profile} key={profile.id}>
                  <div className={styles.profileRow}>
                    <RadioButton
                      name="selected-profile"
                      aria-label={`Select ${profile.name}`}
                      checked={active}
                      onChange={() => selectProfile(profile.id)}
                    />
                    <TextInput
                      className={styles.profileName}
                      wrapperProps={{className: classNames({[styles.unsaved]: isUnsaved('name')})}}
                      aria-label={`${profile.name} profile name`}
                      aria-describedby={isUnsaved('name') ? unsavedHintId : undefined}
                      value={active ? draft.name : profile.name}
                      onFocus={() => {
                        if (!active) selectProfile(profile.id);
                      }}
                      onChange={(event) => updateDraft('name', event.currentTarget.value)}
                    />
                    {(['low', 'mid', 'high'] as const).map((geo) => (
                      <label className={styles.rate} key={geo}>
                        <span>{geo}</span>
                        <TextInput
                          type="number"
                          wrapperProps={{className: classNames({[styles.unsaved]: isUnsaved(geo)})}}
                          aria-label={`${profile.name} ${geo} rate`}
                          aria-describedby={isUnsaved(geo) ? unsavedHintId : undefined}
                          value={
                            active
                              ? draft[geo]
                              : String(
                                  profile[
                                    `annual_${geo}_usd` as
                                      | 'annual_low_usd'
                                      | 'annual_mid_usd'
                                      | 'annual_high_usd'
                                  ]
                                )
                          }
                          onFocus={() => {
                            if (!active) selectProfile(profile.id);
                          }}
                          onChange={(event) => updateDraft(geo, event.currentTarget.value)}
                        />
                      </label>
                    ))}
                    <IconButton
                      variant="borderless"
                      size="medium"
                      aria-label={`Archive ${profile.name}`}
                      onClick={() => archiveMutation.mutate(profile.id)}
                    >
                      <UIIcon src={DeleteLine} size="medium" />
                    </IconButton>
                  </div>
                  <div className={styles.assignments}>
                    {names.length ? (
                      names.map((name) => (
                        <Text tagName="span" size="small" className={styles.chip} key={name}>
                          ▥ {name}
                        </Text>
                      ))
                    ) : (
                      <Text tagName="span" size="small" color="subtle" className={styles.muted}>
                        Not applied yet
                      </Text>
                    )}
                  </div>
                  {hasUnsavedChanges && (
                    <div className={styles.editActions}>
                      <Button
                        variant="primary"
                        onClick={() => saveMutation.mutate()}
                        disabled={!valid(draft) || saveMutation.isPending}
                      >
                        Save profile
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {selectedProfile && (
              <div className={styles.applyRow}>
                <Text tagName="span">
                  Apply “{selectedProfile?.name ?? 'profile'}” for {timeframe} to
                </Text>
                <Popover
                  open={workstreamPickerOpen}
                  onToggle={setWorkstreamPickerOpen}
                  placement="bottom-start"
                  offsetDistance={4}
                  isPortaled={false}
                >
                  <Popover.Trigger asChild>
                    <Button
                      variant="borderless"
                      className={styles.workstreamTrigger}
                      aria-label="Select workstreams"
                    >
                      <Box className={styles.workstreamTriggerContent}>
                        <Text size="small" color="subtle">
                          {selectedWorkstreams.length
                            ? `${selectedWorkstreams.length} workstreams selected`
                            : 'Select workstreams…'}
                        </Text>
                        <UIIcon src={ChevronDownLine} size="small" />
                      </Box>
                    </Button>
                  </Popover.Trigger>
                  <Popover.Overlay>
                    <Popover.Content className={styles.pickerMenu}>
                      <Popover.Body>
                        <Stack gap="8">
                          <TextInput
                            aria-label="Search workstreams"
                            placeholder="Search workstreams…"
                            value={search}
                            onChange={(event) => setSearch(event.currentTarget.value)}
                          />
                          <div className={styles.pickerList}>
                            {filteredWorkstreams.map(({workstream, depth}) => (
                              <label
                                key={workstream.workstream_id}
                                style={{paddingLeft: 4 + depth * 16}}
                              >
                                <Checkbox
                                  checked={selectedWorkstreams.includes(workstream.workstream_id)}
                                  onChange={(event) =>
                                    setSelectedWorkstreams((current) =>
                                      event.currentTarget.checked
                                        ? [...current, workstream.workstream_id]
                                        : current.filter((id) => id !== workstream.workstream_id)
                                    )
                                  }
                                />
                                <Text size="small">{workstream.name}</Text>
                              </label>
                            ))}
                          </div>
                        </Stack>
                      </Popover.Body>
                    </Popover.Content>
                  </Popover.Overlay>
                </Popover>
                <Button
                  variant="opacity"
                  onClick={() => applyMutation.mutate()}
                  disabled={
                    !selectedProfile || assignmentChanges.length === 0 || applyMutation.isPending
                  }
                >
                  Apply
                </Button>
              </div>
            )}
            {(createMutation.error ||
              saveMutation.error ||
              archiveMutation.error ||
              applyMutation.error) && (
              <SystemBanner tone="alert">
                <Text>Could not save this change. Check the values and try again.</Text>
              </SystemBanner>
            )}
          </section>
          <section className={styles.panel} aria-labelledby="orgs-heading">
            <div className={styles.panelHeader}>
              <Text id="orgs-heading" variant="label" size="large" isBold>
                Orgs
              </Text>
              <Text tagName="p" color="subtle" size="small">
                Create orgs, then set one on a pillar’s top row in the Org column. Rows below
                inherit it unless you pick a different org for them.
              </Text>
              <form
                className={styles.addRow}
                onSubmit={(event) => {
                  event.preventDefault();
                  createOrgMutation.mutate();
                }}
              >
                <TextInput
                  wrapperProps={{className: styles.orgNameInput}}
                  aria-label="Org name"
                  placeholder="New org name"
                  // The API limit for org names.
                  maxLength={120}
                  value={orgName}
                  isInvalid={Boolean(orgCreateError)}
                  aria-describedby={orgCreateError ? orgCreateErrorId : undefined}
                  onChange={(event) => {
                    setOrgName(event.currentTarget.value);
                    if (createOrgMutation.error) createOrgMutation.reset();
                  }}
                />
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!orgName.trim() || createOrgMutation.isPending}
                >
                  ＋ Create
                </Button>
              </form>
              {orgCreateError && (
                <Text
                  tagName="span"
                  id={orgCreateErrorId}
                  size="small"
                  color="error"
                  className={styles.fieldError}
                  role="alert"
                >
                  {orgCreateError}
                </Text>
              )}
            </div>
            {!isLoadingData && !dataQuery.error && orgs.length === 0 ? (
              <Text className={styles.status} color="subtle">
                No orgs yet.
              </Text>
            ) : null}
            {orgs.length > 0 && (
              <ul className={styles.orgList} aria-label="Orgs">
                {orgs.map((org) => {
                  const {total, direct} = orgRowCounts.get(org.id) ?? {total: 0, direct: 0};
                  const isInUse = direct > 0;
                  const inUseHintId = `${orgInUseHintPrefix}-${org.id}`;
                  const inUseHint = `Clear “${org.name}” from the ${pluralizeRows(direct)} it’s set on in the Org column before deleting it.`;
                  return (
                    <li className={styles.orgRow} key={org.id}>
                      <Text className={styles.orgName}>{org.name}</Text>
                      <Text size="small" color="subtle" className={styles.orgCount}>
                        {pluralizeWorkstreams(total)}
                        {direct > 0 && direct < total && ` · ${direct} set directly`}
                      </Text>
                      {isInUse && (
                        <span id={inUseHintId} hidden>
                          {inUseHint}
                        </span>
                      )}
                      <Tooltip title={inUseHint} placement="top" isDisabled={!isInUse}>
                        {/* aria-disabled rather than disabled keeps the button focusable, so
                            keyboard and screen reader users still reach the explanation. */}
                        <IconButton
                          variant="borderless"
                          size="medium"
                          className={classNames(styles.orgDelete, {
                            [styles.orgDeleteBlocked]: isInUse,
                          })}
                          aria-label={`Delete ${org.name}`}
                          aria-describedby={isInUse ? inUseHintId : undefined}
                          aria-disabled={isInUse || undefined}
                          disabled={archiveOrgMutation.isPending}
                          onClick={() => {
                            if (!isInUse)
                              archiveOrgMutation.mutate({orgId: org.id, name: org.name});
                          }}
                        >
                          <UIIcon
                            src={DeleteLine}
                            size="medium"
                            className={classNames({[styles.orgDeleteBlockedIcon]: isInUse})}
                          />
                        </IconButton>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            )}
            {orgArchiveError && (
              <SystemBanner tone="alert">
                <Text>{orgArchiveError}</Text>
              </SystemBanner>
            )}
          </section>
        </div>
        <section className={styles.workstreamsSection} aria-labelledby="workstreams-heading">
          <div className={styles.workstreamsHeader}>
            <Text id="workstreams-heading" variant="label" size="large" isBold>
              Workstreams
            </Text>
          </div>
          {pillars.length > 0 && activePillarId != null ? (
            <Tabs
              selectedTab={String(activePillarId)}
              onSelection={(tabId) => setSelectedPillarId(Number(tabId))}
            >
              <Tabs.Group hasHorizontalPadding={false}>
                {pillars.map((pillar) => (
                  <Tabs.Tab key={pillar.workstream_id} id={String(pillar.workstream_id)}>
                    {pillar.name}
                  </Tabs.Tab>
                ))}
              </Tabs.Group>
              <div className={styles.tabsDivider} />
              <Tabs.Panel tabId={String(activePillarId)}>
                {hasHeadcountChanges && (
                  <div className={styles.tableActions}>
                    <Button
                      variant="primary"
                      onClick={() => headcountMutation.mutate(buildHeadcountChanges())}
                      disabled={headcountMutation.isPending || hasInvalidHeadcount}
                      isLoading={headcountMutation.isPending}
                    >
                      Save
                    </Button>
                  </div>
                )}
                <Table
                  columns={tableColumns}
                  data={tableRows}
                  compact
                  columnSizing="share"
                  resizable={false}
                  className={styles.workstreamsTable}
                  scrollAreaLabel="Workstreams cost and headcount data"
                >
                  <Table.Header />
                  <Table.Body>
                    {tableRows.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={tableColumns.length}>
                          <Text color="subtle">No workstreams in this pillar.</Text>
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      <Table.Rows
                        renderRow={(row: WireframeTableRow) => (
                          <>
                            <Table.Row key={row.workstream.workstream_id}>
                              <Table.Cell>
                                <Box
                                  className={styles.workstreamName}
                                  style={{paddingLeft: Math.min(row.depth, 5) * 16}}
                                >
                                  {renderAdjustmentsToggle(row)}
                                  <Text>{row.workstream.name}</Text>
                                </Box>
                              </Table.Cell>
                              <Table.Cell>{renderOrgSelect(row)}</Table.Cell>
                              <Table.Cell style={{padding: 0}}>{renderHeadcount(row)}</Table.Cell>
                              {EDITABLE_COLUMNS.map(({field, label}) => (
                                <Table.Cell key={field} style={{padding: 0}}>
                                  {renderNumberInput(row, field, label)}
                                </Table.Cell>
                              ))}
                              <Table.Cell>
                                <Text color={row.profileName ? undefined : 'subtle'}>
                                  {row.profileName ?? '—'}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Text color={row.estimatedCost == null ? 'subtle' : undefined}>
                                  {row.estimatedCost == null ? '—' : formatUsd(row.estimatedCost)}
                                </Text>
                              </Table.Cell>
                              <Table.Cell style={{padding: 0}}>
                                <div className={styles.tableCellAction}>
                                  {renderClearHeadcount(row)}
                                </div>
                              </Table.Cell>
                            </Table.Row>
                            {isAdjustmentsRowOpen(getRowKey(row)) && renderAdjustmentsRow(row)}
                          </>
                        )}
                      />
                    )}
                  </Table.Body>
                </Table>
              </Tabs.Panel>
            </Tabs>
          ) : isLoadingData ? (
            <Text color="subtle">Loading workstreams…</Text>
          ) : (
            <Text color="subtle">No pillar workstreams found for this planning period.</Text>
          )}
          {headcountMutation.error && (
            <SystemBanner tone="alert" className={styles.saveBanner}>
              {headcountSaveErrors.length > 0 ? (
                <>
                  <Text tagName="p">Unable to save headcount changes:</Text>
                  <ul className={styles.saveErrors}>
                    {headcountSaveErrors.map(({key, text}) => (
                      <li key={key}>
                        <Text>{text}</Text>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <Text>
                  Unable to save headcount changes. Check that each value has up to 2 decimal places
                  and that Growth, CI, and KTLO add up to HC.
                </Text>
              )}
            </SystemBanner>
          )}
        </section>
      </main>
    </Layout.Container>
  );
};
